import { useState } from "react";
import { ArrowRight, Mail, Check, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * High-visibility newsletter widget with double opt-in messaging.
 * Drop it into Home, Inspire, or any other page that needs a conversion moment.
 *
 * Props:
 *  - source: string  (analytics tag, e.g. "home-hero", "inspire-mid")
 *  - variant: "card" | "inline"
 *  - heading: optional override
 *  - subheading: optional override
 */
export default function NewsletterSignup({
  source = "newsletter-widget",
  variant = "card",
  heading,
  subheading,
  testId = "newsletter-signup",
}) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/public/subscribe", { email, source });
      if (data.already_subscribed && !data.pending_confirmation) {
        toast.success("You're already on the list. We see you.");
      } else {
        toast.success("Check your inbox — confirm your email to lock in.");
      }
      setDone(true);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err) || "Subscription failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const wrapperClass =
    variant === "inline"
      ? "py-12 lg:py-16 border-t border-white/5"
      : "py-16 lg:py-24 border-t border-white/5";

  return (
    <section className={wrapperClass} data-testid={testId}>
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <div
          className="relative surface overflow-hidden p-8 lg:p-12"
          style={{
            background:
              "radial-gradient(ellipse 80% 100% at 0% 0%, rgba(232,180,184,0.12), transparent 60%), radial-gradient(ellipse 70% 100% at 100% 100%, rgba(74,14,62,0.4), transparent 60%), #0a050d",
          }}
        >
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 border text-[10px] uppercase tracking-[0.3em]"
                style={{
                  color: "var(--gold)",
                  borderColor: "rgba(232,180,184,0.5)",
                }}
              >
                <Sparkles size={11} />
                Inner Circle · Newsletter
              </div>
              <h2 className="font-serif font-light tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
                {heading || (
                  <>
                    First word.{" "}
                    <em className="italic" style={{ color: "var(--gold)" }}>
                      Every drop.
                    </em>
                  </>
                )}
              </h2>
              <p className="text-white/65 text-base leading-relaxed max-w-xl">
                {subheading ||
                  "Early access to OVA 40 deluxe edits, the upcoming Audio Porn album, weekly Wellness Blog dispatches, and Inner Circle event invites. No noise. No spam. Just the work."}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.25em] text-white/45">
                <span className="inline-flex items-center gap-1.5">
                  <Check size={12} style={{ color: "var(--gold)" }} />
                  Double opt-in
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Check size={12} style={{ color: "var(--gold)" }} />
                  Unsubscribe anytime
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Check size={12} style={{ color: "var(--gold)" }} />
                  Weekly drops, no fluff
                </span>
              </div>
            </div>

            <div className="lg:col-span-5">
              {done ? (
                <div
                  className="surface p-8 text-center space-y-4"
                  data-testid={`${testId}-success`}
                >
                  <div
                    className="w-14 h-14 rounded-full mx-auto flex items-center justify-center border-2"
                    style={{ borderColor: "rgba(232,180,184,0.55)" }}
                  >
                    <Mail size={22} style={{ color: "var(--gold)" }} />
                  </div>
                  <h3 className="font-serif text-2xl tracking-tighter font-light">
                    Check your{" "}
                    <em className="italic" style={{ color: "var(--gold)" }}>
                      inbox.
                    </em>
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    We sent a one-tap confirmation link. Click it to lock your subscription in.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  className="space-y-4"
                  data-testid={`${testId}-form`}
                >
                  <label
                    htmlFor={`${testId}-email`}
                    className="block text-[10px] uppercase tracking-[0.3em] text-white/55"
                  >
                    Your Email
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      id={`${testId}-email`}
                      type="email"
                      required
                      placeholder="you@yourdomain.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 h-12 flex-1"
                      data-testid={`${testId}-email-input`}
                    />
                    <Button
                      type="submit"
                      disabled={busy}
                      className="btn-gold rounded-none px-6 h-12 text-xs whitespace-nowrap inline-flex items-center justify-center gap-2"
                      data-testid={`${testId}-submit`}
                    >
                      {busy ? "Sending…" : "Join the Inner Circle"}
                      <ArrowRight size={14} />
                    </Button>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 leading-relaxed">
                    By subscribing you agree to receive Nina Capone emails. We use double opt-in — you'll get a confirmation email first.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
